// Chakra imports
import {
  Box,
  Icon,
  SimpleGrid,
  useColorModeValue,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  Flex,
} from "@chakra-ui/react";
// Custom components
import MiniStatistics from "components/card/MiniStatistics";
import IconBox from "components/icons/IconBox";
import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import {
  MdAttachMoney,
  MdPeople,
  MdSchool,
  MdPerson,
  MdPayment,
  MdPendingActions,
  MdCheckCircle,
  MdArticle,
  MdBarChart,
  MdGroups,
  MdTrendingUp,
  MdWarning,
} from "react-icons/md";
import Card from "components/card/Card";
import RecentPaymentsTable from "./components/RecentPaymentsTable";
import RevenueChart from "./components/RevenueChart";
import RevenueByStatusChart from "./components/RevenueByStatusChart";
import dashboardService from "services/dashboardService";
import { formatCurrency, formatNumber } from "utils/format";

function objectEntries(data = {}) {
  return Object.entries(data || {}).filter(([, value]) => Number(value) > 0);
}

function sortEntries(entries = []) {
  return [...entries].sort((a, b) => Number(b[1]) - Number(a[1]));
}

function compactEntries(entries = [], maxItems = 6, otherLabel = "Other") {
  const sorted = sortEntries(entries);
  if (sorted.length <= maxItems) return sorted;

  const visible = sorted.slice(0, maxItems);
  const otherTotal = sorted
    .slice(maxItems)
    .reduce((sum, [, value]) => sum + Number(value || 0), 0);

  return otherTotal > 0 ? [...visible, [otherLabel, otherTotal]] : visible;
}

function topEntries(entries = [], maxItems = 10) {
  return sortEntries(entries).slice(0, maxItems);
}

function labelize(value) {
  return String(value || "Unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function AnalyticsCard({ title, icon, children }) {
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const brandColor = useColorModeValue("brand.500", "brand.400");

  return (
    <Card p="24px" w="100%">
      <Flex align="center" gap="10px" mb="18px">
        <Icon as={icon} color={brandColor} boxSize="22px" />
        <Text color={textColor} fontSize="lg" fontWeight="700">
          {title}
        </Text>
      </Flex>
      {children}
    </Card>
  );
}

function EmptyChart({ children }) {
  const textColorSecondary = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  return (
    <Flex minH="240px" align="center" justify="center">
      <Text color={textColorSecondary}>{children}</Text>
    </Flex>
  );
}

export default function AdminDashboard() {
  // Chakra Color Mode
  const brandColor = useColorModeValue("brand.500", "white");
  const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const chartTextColor = useColorModeValue("#2D3748", "#FFFFFF");
  const gridColor = useColorModeValue("#E2E8F0", "#2D3748");
  
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await dashboardService.getDashboardOverview();
        
        if (response.success) {
          setDashboardData(response.data);
        } else {
          setError(response.error || 'Failed to load dashboard data');
        }
      } catch (err) {
        console.error('Dashboard fetch error:', err);
        setError(err.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }} display="flex" justifyContent="center" alignItems="center" minH="400px">
        <Spinner size="xl" color="brand.500" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  if (!dashboardData) {
    return null;
  }

  const { financial, students, tutors, users, system } = dashboardData;
  const studentCenterEntries = topEntries(objectEntries(students?.students_by_center), 10);
  const studentFieldEntries = compactEntries(objectEntries(students?.students_by_field), 6, "Other Fields");
  const tutorStatusEntries = [
    ["Approved", tutors?.approved_tutors || 0],
    ["Pending", tutors?.pending_tutors || 0],
    ["Rejected", tutors?.rejected_tutors || 0],
  ].filter(([, value]) => Number(value) > 0);
  const tutorDepartmentEntries = topEntries(objectEntries(tutors?.tutors_by_department), 10);
  const userRoleEntries = objectEntries(users?.users_by_role);
  const activityEntries = [
    ["Today", system?.activity_logs_today || 0],
    ["This Week", system?.activity_logs_this_week || 0],
    ["This Month", system?.activity_logs_this_month || 0],
  ];
  const tuitionPhaseEntries = topEntries(objectEntries(financial?.revenue_by_tuition_type), 10);
  const paymentStatusEntries = objectEntries(financial?.payments_by_status);

  const chartBase = {
    chart: { toolbar: { show: false }, foreColor: chartTextColor },
    grid: { borderColor: gridColor },
    dataLabels: { enabled: false },
    legend: { position: "bottom", labels: { colors: chartTextColor } },
  };

  const donutOptions = (labels, colors) => ({
    ...chartBase,
    labels,
    colors,
    legend: {
      position: "bottom",
      horizontalAlign: "center",
      fontSize: "12px",
      labels: { colors: chartTextColor },
      itemMargin: { horizontal: 8, vertical: 4 },
    },
    stroke: { width: 0 },
    dataLabels: { enabled: true, formatter: (value) => `${value.toFixed(0)}%` },
  });

  const barOptions = (categories, colors = ["#2563EB"], horizontal = false) => ({
    ...chartBase,
    colors,
    xaxis: {
      categories,
      labels: { style: { colors: chartTextColor } },
    },
    yaxis: {
      labels: {
        maxWidth: horizontal ? 180 : undefined,
        style: { colors: chartTextColor, fontSize: "12px" },
      },
    },
    plotOptions: {
      bar: {
        horizontal,
        borderRadius: 5,
        columnWidth: horizontal ? undefined : "48%",
      },
    },
  });

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      {/* Financial Statistics */}
      <Text fontSize="2xl" fontWeight="700" color={textColor} mb="20px">
        Financial Overview
      </Text>
      <SimpleGrid
        columns={{ base: 1, md: 2, lg: 4 }}
        gap='20px'
        mb='20px'>
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={
                <Icon w='32px' h='32px' as={MdAttachMoney} color={brandColor} />
              }
            />
          }
          name='Total Paid'
          value={formatCurrency(financial?.total_paid || 0)}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={
                <Icon w='32px' h='32px' as={MdPendingActions} color={brandColor} />
              }
            />
          }
          name='Pending Payments'
          value={formatCurrency(financial?.total_pending || 0)}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={
                <Icon w='32px' h='32px' as={MdCheckCircle} color={brandColor} />
              }
            />
          }
          name='Validated Payments'
          value={formatCurrency(financial?.total_validated_payments || 0)}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={
                <Icon w='32px' h='32px' as={MdPayment} color={brandColor} />
              }
            />
          }
          name='Total Tuitions'
          value={formatNumber(financial?.total_tuitions || 0)}
        />
      </SimpleGrid>

      <SimpleGrid
        columns={{ base: 1, md: 2, lg: 4 }}
        gap='20px'
        mb='20px'>
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={<Icon w='32px' h='32px' as={MdAttachMoney} color='green.500' />}
            />
          }
          name='Tuition Value'
          value={formatCurrency(financial?.total_tuition_amount || 0)}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={<Icon w='32px' h='32px' as={MdWarning} color='red.500' />}
            />
          }
          name='Overdue Tuition'
          value={formatCurrency(financial?.total_overdue || 0)}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={<Icon w='32px' h='32px' as={MdGroups} color='cyan.500' />}
            />
          }
          name='With Tuitions'
          value={formatNumber(students?.students_with_tuitions || 0)}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={<Icon w='32px' h='32px' as={MdPendingActions} color='orange.500' />}
            />
          }
          name='Without Tuitions'
          value={formatNumber(students?.students_without_tuitions || 0)}
        />
      </SimpleGrid>

      {/* Students, Tutors, Users Statistics */}
      <Text fontSize="2xl" fontWeight="700" color={textColor} mb="20px" mt="40px">
        Overview Statistics
      </Text>
      <SimpleGrid
        columns={{ base: 1, md: 2, lg: 4 }}
        gap='20px'
        mb='20px'>
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={
                <Icon w='32px' h='32px' as={MdSchool} color={brandColor} />
              }
            />
          }
          name='Total Students'
          value={formatNumber(students?.total_students || 0)}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={
                <Icon w='32px' h='32px' as={MdPeople} color={brandColor} />
              }
            />
          }
          name='Total Tutors'
          value={formatNumber(tutors?.total_applications || 0)}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={
                <Icon w='32px' h='32px' as={MdPerson} color={brandColor} />
              }
            />
          }
          name='Total Users'
          value={formatNumber(users?.total_users || 0)}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={
                <Icon w='32px' h='32px' as={MdArticle} color={brandColor} />
              }
            />
          }
          name='Total Blogs'
          value={formatNumber(system?.total_blogs || 0)}
        />
      </SimpleGrid>

      {/* Charts */}
      <SimpleGrid columns={{ base: 1, md: 2, xl: 2 }} gap='20px' mb='20px'>
        <RevenueChart 
          revenueByPaymentMethod={financial?.revenue_by_payment_method || {}}
          revenueByStatus={financial?.revenue_by_status || {}}
        />
        <RevenueByStatusChart 
          revenueByStatus={financial?.revenue_by_status || {}}
        />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, xl: 2 }} gap='20px' mb='20px'>
        <AnalyticsCard icon={MdSchool} title='Students by Center'>
          {studentCenterEntries.length ? (
            <ReactApexChart
              options={barOptions(studentCenterEntries.map(([label]) => label), ["#2563EB"], true)}
              series={[{ name: "Students", data: studentCenterEntries.map(([, value]) => value) }]}
              type='bar'
              width='100%'
              height={330}
            />
          ) : (
            <EmptyChart>No student center data yet.</EmptyChart>
          )}
        </AnalyticsCard>

        <AnalyticsCard icon={MdBarChart} title='Students by Field'>
          {studentFieldEntries.length ? (
            <ReactApexChart
              options={donutOptions(studentFieldEntries.map(([label]) => labelize(label)), ["#2563EB", "#16A34A", "#F59E0B", "#0891B2"])}
              series={studentFieldEntries.map(([, value]) => value)}
              type='donut'
              width='100%'
              height={330}
            />
          ) : (
            <EmptyChart>No student field data yet.</EmptyChart>
          )}
        </AnalyticsCard>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, xl: 2 }} gap='20px' mb='20px'>
        <AnalyticsCard icon={MdPeople} title='Tutors by Status'>
          {tutorStatusEntries.length ? (
            <ReactApexChart
              options={donutOptions(tutorStatusEntries.map(([label]) => label), ["#16A34A", "#F59E0B", "#DC2626"])}
              series={tutorStatusEntries.map(([, value]) => value)}
              type='donut'
              width='100%'
              height={320}
            />
          ) : (
            <EmptyChart>No tutor status data yet.</EmptyChart>
          )}
        </AnalyticsCard>

        <AnalyticsCard icon={MdGroups} title='Tutors by Department'>
          {tutorDepartmentEntries.length ? (
            <ReactApexChart
              options={barOptions(tutorDepartmentEntries.map(([label]) => labelize(label)), ["#0891B2"], true)}
              series={[{ name: "Tutors", data: tutorDepartmentEntries.map(([, value]) => value) }]}
              type='bar'
              width='100%'
              height={320}
            />
          ) : (
            <EmptyChart>No tutor department data yet.</EmptyChart>
          )}
        </AnalyticsCard>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, xl: 2 }} gap='20px' mb='20px'>
        <AnalyticsCard icon={MdPerson} title='Users by Role'>
          {userRoleEntries.length ? (
            <ReactApexChart
              options={donutOptions(userRoleEntries.map(([label]) => labelize(label)), ["#2563EB", "#16A34A", "#F59E0B", "#0891B2"])}
              series={userRoleEntries.map(([, value]) => value)}
              type='donut'
              width='100%'
              height={320}
            />
          ) : (
            <EmptyChart>No user role data yet.</EmptyChart>
          )}
        </AnalyticsCard>

        <AnalyticsCard icon={MdTrendingUp} title='Activity Logs'>
          <ReactApexChart
            options={barOptions(activityEntries.map(([label]) => label), ["#7C3AED"])}
            series={[{ name: "Activity", data: activityEntries.map(([, value]) => value) }]}
            type='bar'
            width='100%'
            height={320}
          />
        </AnalyticsCard>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, xl: 2 }} gap='20px' mb='20px'>
        <AnalyticsCard icon={MdPayment} title='Tuition Amount by Phase'>
          {tuitionPhaseEntries.length ? (
            <ReactApexChart
              options={barOptions(tuitionPhaseEntries.map(([label]) => labelize(label)), ["#0891B2"], true)}
              series={[{ name: "Amount", data: tuitionPhaseEntries.map(([, value]) => value) }]}
              type='bar'
              width='100%'
              height={320}
            />
          ) : (
            <EmptyChart>No tuition phase data yet.</EmptyChart>
          )}
        </AnalyticsCard>

        <AnalyticsCard icon={MdCheckCircle} title='Payments by Status'>
          {paymentStatusEntries.length ? (
            <ReactApexChart
              options={donutOptions(paymentStatusEntries.map(([label]) => labelize(label)), ["#16A34A", "#F59E0B", "#DC2626"])}
              series={paymentStatusEntries.map(([, value]) => value)}
              type='donut'
              width='100%'
              height={320}
            />
          ) : (
            <EmptyChart>No payment status data yet.</EmptyChart>
          )}
        </AnalyticsCard>
      </SimpleGrid>

      {/* Recent Payments Table */}
      <Box mb='20px'>
        <RecentPaymentsTable 
          tableData={financial?.recent_payments || []}
        />
      </Box>
    </Box>
  );
}
